import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layers3, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND}${url}`);

export default function CardShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/cardshare/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setError("This card link is invalid or was removed."));
  }, [token]);

  if (error) {
    return (
      <div data-testid="card-share-error" className="min-h-screen bg-[#05060f] text-white flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-zinc-400">{error}</p>
        <Link to="/" className="text-[#A78BFA] hover:text-white text-sm">Go to CardArk</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#05060f] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-[#A78BFA] rounded-full animate-spin" /></div>;
  }

  const { card, owner_name } = data;
  const images = [
    card.captured_image_url && { url: card.captured_image_url, label: "Snapshot" },
    card.reference_image_url && { url: card.reference_image_url, label: "Reference" },
  ].filter(Boolean);

  return (
    <div data-testid="card-share-page" className="min-h-screen bg-[#05060f] text-white px-6 py-12">
      <Seo title={`${card.name} — CardArk`} description={`${card.name} from ${owner_name}'s binder on CardArk`} />
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="flex items-center gap-2">
            <Layers3 className="w-6 h-6 text-[#A78BFA]" />
            <span className="font-display font-bold text-lg">Card<span className="text-[#A78BFA]">Ark</span></span>
          </Link>
          <span className="text-xs text-zinc-500">{owner_name}'s binder · shared card</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          <div className={`grid ${images.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-4`}>
            {images.length ? images.map((img, i) => (
              <div key={i}>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{img.label}</p>
                <img data-testid={`card-share-image-${i}`} src={resolveUrl(img.url)} alt={card.name} className="w-full aspect-[3/4] object-cover rounded-xl border border-white/10" />
              </div>
            )) : (
              <div className="w-full aspect-[3/4] flex items-center justify-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-xl">No photo</div>
            )}
          </div>

          <div className="space-y-5">
            <div>
              <h1 data-testid="card-share-name" className="font-display text-3xl font-bold">{card.name}</h1>
              <p className="text-sm text-zinc-500 mt-1">
                {card.set_name || "Unknown set"} {card.card_number ? `· #${card.card_number}` : ""} {card.rarity ? `· ${card.rarity}` : ""}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {card.is_graded ? (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-sky-400/30 bg-sky-500/10 text-sky-300">
                    Graded · {[card.grading_company, card.grade].filter(Boolean).join(" ") || "slab"}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-zinc-300">
                    Condition: {card.condition || "NM"}
                  </span>
                )}
                {card.match_verified && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-300 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
            </div>

            <div className="border border-white/10 bg-[#111113] rounded-xl p-6">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Market Value</p>
              <p data-testid="card-share-price" className="font-mono-plex text-3xl font-bold text-[#A78BFA]">
                {card.current_price != null ? `$${Number(card.current_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "No price data"}
              </p>
              <p className="text-xs text-zinc-500 mt-1">priced with real eBay last-sold data</p>
            </div>

            <div className="border border-white/10 bg-[#111113] rounded-xl p-6">
              <p className="text-sm text-zinc-300">Got a stack of cards? Film them and let AI build your priced binder.</p>
              <Link
                to="/"
                data-testid="card-share-cta"
                className="inline-block mt-3 text-sm font-semibold text-black bg-[#A78BFA] hover:bg-[#8B5CF6] transition-colors rounded-full px-5 py-2"
              >
                Try CardArk free
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
